import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { handleApiErrors } from 'helpers';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { FieldError, SubmitHandler, useForm } from 'react-hook-form';
import { GeographicalZone } from 'types/interfaces/GeographicalZone';
import { GeographicalZoneAddress } from 'types/interfaces/GeographicalZoneAddress';
import { ObjectForSelection } from 'types/interfaces/ObjectForSelection';
import { GeographicalZoneCardProps } from 'types/props/GeographicalZoneCardProps';

import { AvgTransportSpeedService } from 'components/services/AvgTransportSpeedService';
import { GeographicalZoneService } from 'components/services/GeographicalZoneService';
import { GeographicalZoneAddressSchema } from 'constants/schemas/GeographicalZoneAddressSchema';
import { GeographicalZoneSchema } from 'constants/schemas/GeographicalZoneSchema';
import ConfirmationDialog from 'ui/shared/ConfirmationDialog';


import '../ui/css/EmployeeCard.css';
import { CustomDropdown } from './custom-controls';
import AddressElementDialog from './detailedInformationCard/AddressElementDialog';
import { DepartmentZonesList } from './detailedInformationCard/DepartmentZonesList';
import DeleteConfirmationDialog from './shared/DeleteConfirmationDialog';

interface RefInterface {
  handleCancel: () => void;
}

const GeographicalZoneCard = React.forwardRef<RefInterface, GeographicalZoneCardProps>(
  ({
    geographicalZoneProps,
    onAdd,
    onUpdate,
    onDelete,
    closeDialog,
    readOnly = false
  }: GeographicalZoneCardProps, ref) => {
    const {
      register,
      handleSubmit,
      watch,
      setValue,
      reset,
      setError,
      formState: { isDirty, errors }
    } = useForm<GeographicalZone>({
      resolver: zodResolver(GeographicalZoneSchema),
      defaultValues: {
        ...geographicalZoneProps
      }
    }); 

    const name = watch('name');
    const addresses = watch('addresses') ?? [];
    const avgTransportSpeed = watch('avgTransportSpeed');
    const toast = useRef<Toast>(null);
    const [showDialog, setShowDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [addressDialogVisible, setAddressDialogVisible] = useState(false);
    const [editingAddressIndex, setEditingAddressIndex] = useState<number | null>(null);
    const [avgTransportSpeedOptions, setAvgTransportSpeedOptions] = useState<
      ObjectForSelection[]
    >([]);

    const addressFormMethods = useForm<GeographicalZoneAddress>({
      resolver: zodResolver(GeographicalZoneAddressSchema as any)
    });

    const loadGeographicalZoneById = useCallback(() => {
      const id = geographicalZoneProps.id;
      const parsedId = typeof id === 'string' ? parseInt(id, 10) : id;
      if (!parsedId || isNaN(Number(parsedId)) || Number(parsedId) <= 0) {
        reset({
          ...geographicalZoneProps,
          addresses: geographicalZoneProps.addresses ?? [],
          avgTransportSpeed: geographicalZoneProps.avgTransportSpeed ?? null
        });
        return;
      }

      GeographicalZoneService.getGeographicalZoneById(Number(parsedId)).then(
        (resp) => {
          if (resp.data) {
            reset({
              ...resp.data,
              addresses: resp.data.addresses ?? [],
              avgTransportSpeed: resp.data.avgTransportSpeed ?? null
            });
          }
        }
      );
    }, [geographicalZoneProps, reset]);

    const loadAvgTransportSpeedOptions = useCallback(() => {
      AvgTransportSpeedService.getForSelection()
        .then((resp) => {
          setAvgTransportSpeedOptions(resp.data ?? []);
        })
        .catch(() => {
          setAvgTransportSpeedOptions([]);
          toast.current?.show({
            severity: 'warn',
            summary: 'Предупреждение',
            detail: 'Не удалось загрузить средние скорости движения'
          });
        });
    }, []);

    useEffect(() => {
      loadGeographicalZoneById();
    }, [loadGeographicalZoneById]);

    useEffect(() => {
      loadAvgTransportSpeedOptions();
    }, [loadAvgTransportSpeedOptions]);

    const onSubmit: SubmitHandler<GeographicalZone> = async (data) => {
      const newData: GeographicalZone = { ...data };

      const parsedId =
        typeof data.id === 'string' ? parseInt(data.id, 10) : data.id;
      const isIdExist =
        parsedId !== undefined && parsedId !== null && !isNaN(Number(parsedId)) && Number(parsedId) > 0;

      let result;
      if (onUpdate && isIdExist) {
        result = await onUpdate(Number(parsedId), newData);
      }
      if (onAdd && !isIdExist) {
        newData.id = 0;
        result = await onAdd(newData);
      }

      if (result?.data?.isSuccess) {
        closeDialog?.();
        return;
      }

      handleApiErrors({
        error: result?.data?.error,
        setError,
        fieldMapping: {
          SameName: {
            field: 'name',
            message:
              'Запись с таким названием уже существует. Пожалуйста, укажите уникальное название.'
          }
        }
      });

      if (!result?.data?.error) {
        toast.current?.show({
          severity: 'error',
          summary: 'Ошибка сохранения',
          detail: 'Не удалось сохранить географическую зону'
        });
      }
    };

    const onError = () => {
      toast.current?.show({
        severity: 'error',
        summary: 'Ошибка валидации',
        detail: 'Проверьте заполнение полей',
        life: 3000
      });
    };

    const handleCancel = () => {
      if (isDirty) {
        setShowDialog(true);
      } else {
        closeDialog?.();
      }
    };

    useImperativeHandle(ref, () => ({ handleCancel }));

    const handleConfirmationCancel = () => setShowDialog(false);
    const handleConfirmationContinue = () => {
      setShowDialog(false);
      closeDialog?.();
    };

    const handleAddAddress = useCallback((area: GeographicalZoneAddress) => {
      const current = (addresses ?? []) as GeographicalZoneAddress[];
      if (editingAddressIndex !== null) {
        const updated = [...current];
        updated[editingAddressIndex] = area;
        setValue('addresses', updated, { shouldDirty: true, shouldValidate: true });
      } else {
        setValue('addresses', [...current, area], { shouldDirty: true, shouldValidate: true });
      }
      setEditingAddressIndex(null);
    }, [addresses, editingAddressIndex, setValue]);

    const onDeleteAddress = useCallback((index: number) => {
      const current = (addresses ?? []) as GeographicalZoneAddress[];
      const updated = current.filter((_, i) => i !== index);
      setValue('addresses', updated, { shouldDirty: true, shouldValidate: true });
    }, [addresses, setValue]);

    const openAddAddressDialog = useCallback(() => {
      addressFormMethods.reset({
        id: 0,
        isFias: true,
        cityFias: null,
        streetFias: null,
        houseFias: null,
        excludedStreetsFias: [],
        excludedHousesFias: [],
        customCity: '',
        customStreet: '',
        customHouse: '',
        region: '',
        county: '',
        district: '',
        latitude: null,
        longitude: null
      } as any);
      setEditingAddressIndex(null);
      setAddressDialogVisible(true);
    }, [addressFormMethods]);

    const openEditAddressDialog = useCallback((index: number) => {
      const area = (addresses ?? [])[index] as GeographicalZoneAddress;
      addressFormMethods.reset({
        ...area,
        excludedStreetsFias: area.excludedStreetsFias ?? [],
        excludedHousesFias: area.excludedHousesFias ?? []
      } as any);
      setEditingAddressIndex(index);
      setAddressDialogVisible(true);
    }, [addressFormMethods, addresses]);

    const handleDeleteEntity = useCallback(async () => {
      const id = watch('id');
      if (!onDelete) return;
      if (id === undefined || id === null) return;
      const parsedId = typeof id === 'string' ? parseInt(id, 10) : id;
      if (!parsedId || isNaN(Number(parsedId)) || Number(parsedId) <= 0) return;
      await onDelete(parsedId);
      closeDialog?.();
    }, [closeDialog, onDelete, watch]);

    const addressesTitle = useMemo(() => 'Адресные элементы', []);

    return (
      <div
        className="dialog-tabless-card geographicalZones-card__roles"
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Toast ref={toast} />
        <Divider />

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr)',
            gridTemplateRows: 'auto auto auto auto 1fr auto'
          }}
        >
          <div className="flex flex-row gap-5">
            <div className="w-full">
              <div className="flex flex-row justify-content-between align-items-center gap-3 w-full">
                <label htmlFor="name" className="col p-0">
                  Название<span className="required">*</span>:
                </label>
                <div className="col-8 p-0">
                  <InputText
                    className="user-card__input w-full"
                    disabled={readOnly}
                    {...register('name')}
                  />
                </div>
              </div>
              <p
                className={`card-errors ${
                  errors.name ? 'text-red-500' : 'text-transparent'
                }`}
              >
                {errors.name?.message ? String(errors.name.message) : ' '}
              </p>
            </div>
          </div>

          <div className="flex flex-row gap-5">
            <div className="w-full">
              <div className="flex flex-row justify-content-between align-items-center gap-3 w-full">
                <label htmlFor="description" className="col p-0">
                  Описание:
                </label>
                <div className="col-8 p-0">
                  <InputText
                    className="user-card__input w-full"
                    disabled={readOnly}
                    {...register('description')}
                  />
                </div>
              </div>
              <p
                className={`card-errors ${
                  errors.description ? 'text-red-500' : 'text-transparent'
                }`}
              >
                {errors.description?.message ? String(errors.description.message) : ' '}
              </p>
            </div>
          </div>

          <div className="flex flex-row gap-5">
            <div className="w-full">
              <div className="flex flex-row justify-content-between align-items-center gap-3 w-full">
                <label className="col p-0">
                  Средняя скорость движения<span className="required">*</span>:
                </label>
                <div className="col-8 p-0">
                  <CustomDropdown<ObjectForSelection>
                    passedClassName="w-full"
                    assetOptions={avgTransportSpeedOptions}
                    selectedAsset={avgTransportSpeed ?? null}
                    disabled={readOnly}
                    onSelectionChange={(value) =>
                      setValue(
                        'avgTransportSpeed',
                        (value as ObjectForSelection | null) ?? null,
                        {
                          shouldDirty: true,
                          shouldValidate: true
                        }
                      )
                    }
                    fieldName="label"
                    placeholder="Выберите среднюю скорость движения"
                    customHeight={280}
                    showDeletedWarning
                    deletedWarningTooltip="Удалено в справочнике"
                  />
                </div>
              </div>
              <p
                className={`card-errors ${
                  errors.avgTransportSpeed ? 'text-red-500' : 'text-transparent'
                }`}
              >
                {(errors.avgTransportSpeed as FieldError | undefined)?.message
                  ? String((errors.avgTransportSpeed as FieldError).message)
                  : ' '}
              </p>
            </div>
          </div>

          <div className="destination-areas-section" style={{ paddingTop: '0.5rem' }}>
            <h4 className="m-0">{addressesTitle}</h4>
            <DepartmentZonesList
              areas={addresses as any[]}
              onEdit={(index) => openEditAddressDialog(index)}
              onDelete={(index) => onDeleteAddress(index)}
              entityType="адресный элемент"
              readOnly={readOnly}
            />
            {!readOnly && (
              <Button
                label="Добавить элемент географической зоны"
                icon="pi pi-plus"
                className="p-button-outlined mt-2"
                onClick={openAddAddressDialog}
              />
            )}
          </div>

          <div style={{ paddingTop: '1rem' }}>
            <div className="flex gap-3">
              {!readOnly && (
                <Button
                  label="Сохранить"
                  icon="pi pi-check"
                  onClick={handleSubmit(onSubmit, onError)}
                />
              )}
              {!readOnly && (
                <Button
                  label="Удалить"
                  icon="pi pi-trash"
                  onClick={() => setShowDeleteDialog(true)}
                  className="p-button-danger"
                />
              )}
              <Button
                label="Отмена"
                icon="pi pi-times"
                onClick={handleCancel}
                className="p-button-secondary"
              />
            </div>
          </div>
        </div>

        <ConfirmationDialog
          visible={showDialog}
          onCancel={handleConfirmationCancel}
          onContinue={handleConfirmationContinue}
        />

        <DeleteConfirmationDialog
          visible={showDeleteDialog}
          onHide={() => setShowDeleteDialog(false)}
          onConfirm={() => {
            setShowDeleteDialog(false);
            handleDeleteEntity();
          }}
          entityType="географическую зону"
          entityName={name}
        />

        <AddressElementDialog
          visible={addressDialogVisible}
          onHide={() => setAddressDialogVisible(false)}
          onSubmit={handleAddAddress}
          initialData={
            editingAddressIndex !== null && addresses.length > editingAddressIndex
              ? (addresses[editingAddressIndex] as any)
              : undefined
          }
          formMethods={addressFormMethods as any}
          headerAdd="Добавление адресного элемента"
          headerEdit="Редактирование адресного элемента"
          enableAddressExclusions
        />
      </div>
    );
  }
);

GeographicalZoneCard.displayName = 'GeographicalZoneCard';

export default GeographicalZoneCard;

